import { CURRENT_CONTENT_SCHEMA_VERSION } from "@note-app/contracts";
import type {
  AttachmentRecord,
  AttachmentRepository,
  BlobStore,
} from "@note-app/knowledge-services";

import {
  PlatformRuntimeError,
} from "./capabilities.ts";
import type {
  AttachmentUpload,
  PlatformAttachmentPort,
} from "./ports.ts";

export class BrowserAttachmentPort implements PlatformAttachmentPort {
  private readonly urls = new Set<string>();

  public constructor(
    private readonly repository: AttachmentRepository,
    private readonly blobs: BlobStore,
    private readonly nextId: () => string = () => crypto.randomUUID(),
    private readonly now: () => number = Date.now,
    private readonly operationTimeoutMs: number = 30_000,
  ) {}

  async upload(file: File, signal?: AbortSignal): Promise<AttachmentUpload> {
    throwIfAborted(signal);
    const bytes = new Uint8Array(
      await waitForOperation(
        file.arrayBuffer(),
        signal,
        this.operationTimeoutMs,
        "Reading the selected attachment took too long.",
      ),
    );
    throwIfAborted(signal);
    const sha256 = hex(
      await waitForOperation(
        crypto.subtle.digest("SHA-256", new Uint8Array(bytes).buffer),
        signal,
        this.operationTimeoutMs,
        "Preparing the selected attachment took too long.",
      ),
    );
    const existing = await waitForOperation(
      this.repository.getBySha256(sha256),
      signal,
      this.operationTimeoutMs,
      "Checking the attachment library took too long.",
    );
    if (existing) {
      if (
        (await waitForOperation(
          this.blobs.stat(existing.storageKey),
          signal,
          this.operationTimeoutMs,
          "Checking the stored attachment took too long.",
        )) === null
      ) {
        await this.writeBlob(existing.storageKey, bytes, signal);
      }
      return { attachment: existing, deduplicated: true };
    }
    const storageKey = sha256;
    await this.writeBlob(storageKey, bytes, signal);
    const timestamp = this.now();
    const attachmentId = this.nextId();
    const candidate: AttachmentRecord = {
      id: attachmentId,
      attachmentId,
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
      revision: 1,
      createdAt: timestamp,
      updatedAt: timestamp,
      sha256,
      mimeType: file.type || "application/octet-stream",
      byteLength: bytes.byteLength,
      originalName: file.name || "attachment",
      metadata: {},
      storageKey,
      referenceCount: 0,
      unreferencedAt: timestamp,
    };
    const stored = await waitForOperation(
      this.repository.createIfAbsent(candidate),
      signal,
      this.operationTimeoutMs,
      "Saving attachment metadata took too long.",
    );
    return {
      attachment: stored.record,
      deduplicated: !stored.created,
    };
  }

  async resolve(attachmentId: string, signal?: AbortSignal): Promise<string> {
    throwIfAborted(signal);
    const attachment = await waitForOperation(
      this.repository.get(attachmentId),
      signal,
      this.operationTimeoutMs,
      "Loading attachment metadata took too long.",
    );
    if (!attachment) {
      throw new PlatformRuntimeError(
        "not-found",
        `Attachment ${attachmentId} was not found.`,
        "attachmentStorage",
      );
    }
    const chunks: Uint8Array[] = [];
    const iterator = this.blobs.read(attachment.storageKey)[Symbol.asyncIterator]();
    while (true) {
      const result = await waitForOperation(
        iterator.next(),
        signal,
        this.operationTimeoutMs,
        "Loading the attachment preview took too long.",
      );
      if (result.done) break;
      throwIfAborted(signal);
      chunks.push(result.value);
    }
    const url = URL.createObjectURL(
      new Blob(
        chunks.map((chunk) => new Uint8Array(chunk).buffer),
        { type: attachment.mimeType },
      ),
    );
    this.urls.add(url);
    return url;
  }

  release(url: string): void {
    if (!this.urls.delete(url)) return;
    URL.revokeObjectURL(url);
  }

  releaseAll(): void {
    for (const url of this.urls) URL.revokeObjectURL(url);
    this.urls.clear();
  }

  private async writeBlob(
    storageKey: string,
    bytes: Uint8Array,
    signal?: AbortSignal,
  ): Promise<void> {
    const temp = await waitForOperation(
      this.blobs.createTemp(),
      signal,
      this.operationTimeoutMs,
      "Preparing attachment storage took too long.",
    );
    try {
      await waitForOperation(
        this.blobs.appendTemp(temp, bytes),
        signal,
        this.operationTimeoutMs,
        "Preparing attachment bytes took too long.",
      );
      await waitForOperation(
        this.blobs.commitTemp(temp, storageKey),
        signal,
        this.operationTimeoutMs,
        "Saving the attachment took too long.",
      );
    } catch (error) {
      void this.blobs.discardTemp(temp).catch(() => undefined);
      throw error;
    }
  }
}

class AttachmentOperationTimeoutError extends PlatformRuntimeError {
  public constructor(message: string) {
    super("operation-failed", message, "attachmentStorage");
    this.name = "AttachmentOperationTimeoutError";
  }
}

function waitForOperation<T>(
  operation: PromiseLike<T>,
  signal: AbortSignal | undefined,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> {
  throwIfAborted(signal);
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      callback();
    };
    const onAbort = () =>
      finish(() =>
        reject(
          signal?.reason instanceof Error
            ? signal.reason
            : new DOMException("Operation cancelled", "AbortError"),
        ),
      );
    const timer = globalThis.setTimeout(
      () => finish(() => reject(new AttachmentOperationTimeoutError(timeoutMessage))),
      Math.max(1, timeoutMs),
    );
    signal?.addEventListener("abort", onAbort, { once: true });
    Promise.resolve(operation).then(
      (value) => finish(() => resolve(value)),
      (error: unknown) => finish(() => reject(error)),
    );
  });
}

function hex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("Operation cancelled", "AbortError");
  }
}
